import { type FormInstance } from 'antd';
import type { StoreApi } from 'zustand';

import { registerUser } from '~/services/auth/AuthService';
import type { IRegisterParams } from '~/types/api/Auth';
import type { IAuthStore } from './useAuthStore';

export type IRegisterFormFilter = {
  isAccountOwner: boolean;
  cpf?: string;
  fullName: string;
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
};

export type IRegisterActions = {
  resetStates: () => void;
  setFormFilterInstance: (form: Partial<FormInstance<IRegisterFormFilter>>) => void;
  getParams: () => IRegisterParams;
  handleSubmit: () => Promise<void>;
};

export type IRegisterState = {
  isLoading: boolean;
  formFilterInstance: Partial<FormInstance<IRegisterFormFilter>> | null;
};

export type IRegisterSlice = IRegisterState & IRegisterActions;

const useRegisterSlice = (
  set: StoreApi<IAuthStore>['setState'],
  get: StoreApi<IAuthStore>['getState'],
): IRegisterSlice => ({
  // States
  isLoading: false,
  formFilterInstance: null,

  // Setters
  setFormFilterInstance: form => {
    get().setData(state => {
      state.registerSlice.formFilterInstance = form;
    });
  },

  // Actions
  resetStates: () => {
    get().setData(state => {
      state.registerSlice.isLoading = false;
      state.registerSlice.formFilterInstance = null;
    });
  },
  getParams: () => {
    const { formFilterInstance } = get().registerSlice;
    if (!formFilterInstance?.getFieldsValue) {
      throw new Error('Instância do formulário não está definida.');
    }

    const formValues = formFilterInstance.getFieldsValue();
    const isOwner = formValues.isAccountOwner;

    if (formValues.password !== formValues.confirmPassword) {
      throw new Error('As senhas devem ser iguais!');
    } else if (formValues.email !== formValues.confirmEmail) {
      throw new Error('Os e-mails devem ser iguais!');
    }

    return {
      cpf: isOwner ? formValues.cpf : undefined,
      email: formValues.email,
      confirmEmail: formValues.confirmEmail,
      fullName: formValues.fullName,
      password: formValues.password,
      confirmPassword: formValues.confirmPassword,
      isAccountOwner: isOwner,
    } as IRegisterParams;
  },
  handleSubmit: async () => {
    const { formFilterInstance } = get().registerSlice;
    if (!formFilterInstance) return;

    get().setData(state => {
      state.registerSlice.isLoading = true;
    });

    try {
      const params = get().registerSlice.getParams();
      await registerUser(params);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    } finally {
      get().setData(state => {
        state.registerSlice.isLoading = false;
      });
    }
  },
});

export default useRegisterSlice;
